// Wraps children and shakes them whenever `trigger` changes to a new truthy value.
// defib: a hard jolt that decays (wrong answers). charge: a buzzing build-up (double points stamp). nudge: a gentle
// double tap. Transform only. Reduced motion: no shake (the state change itself carries the meaning).
import { useRef, type ReactNode } from 'react';
import { useFire } from './fire';
import { canAnimate, prefersReducedMotion } from './reduced';

export type ShakeKind = 'defib' | 'charge' | 'nudge';
const FRAMES: Record<ShakeKind, { frames: Keyframe[]; duration: number }> = {
  defib: { duration: 460, frames: [
    { transform: 'translate3d(0,0,0) rotate(0)' }, { transform: 'translate3d(-12px,0,0) rotate(-1.4deg)', offset: 0.1 },
    { transform: 'translate3d(10px,0,0) rotate(1.1deg)', offset: 0.26 }, { transform: 'translate3d(-7px,0,0) rotate(-0.6deg)', offset: 0.44 },
    { transform: 'translate3d(4px,0,0)', offset: 0.62 }, { transform: 'translate3d(-2px,0,0)', offset: 0.8 }, { transform: 'translate3d(0,0,0)' },
  ] },
  charge: { duration: 760, frames: Array.from({ length: 17 }, (_, i) => {
    const t = i / 16; const amp = i === 16 ? 0 : 0.6 + 3 * t;
    return { transform: `translate3d(${i % 2 ? amp : -amp}px,${i % 3 === 0 ? -amp * 0.4 : amp * 0.3}px,0)`, offset: t };
  }) },
  nudge: { duration: 420, frames: [
    { transform: 'translate3d(0,0,0)' }, { transform: 'translate3d(-5px,0,0)', offset: 0.2 }, { transform: 'translate3d(5px,0,0)', offset: 0.45 },
    { transform: 'translate3d(-2px,0,0)', offset: 0.7 }, { transform: 'translate3d(0,0,0)' },
  ] },
};

export function Shake({ trigger, children, className = '', kind = 'defib' }: { trigger: unknown; children: ReactNode; className?: string; kind?: ShakeKind }) {
  const ref = useRef<HTMLDivElement>(null);
  useFire(trigger, () => {
    const el = ref.current; if (!el || !canAnimate() || prefersReducedMotion()) return;
    const { frames, duration } = FRAMES[kind];
    const a = el.animate(frames, { duration, easing: kind === 'charge' ? 'linear' : 'ease-out' });
    return () => a.cancel();
  });
  return <div ref={ref} className={className}>{children}</div>;
}
